import CalendarIcon from "@uui/Calendar";
import { capitalize, find, flatten, replace } from "lodash/fp";
import { DateTime } from "luxon";

import ButtonGroup from "@mui/material/ButtonGroup";
import Typography from "@mui/material/Typography";

import {
  Menu,
  MenuButton,
} from "@app/analytics/components/DateSelector/styles";
import { getDateLabel } from "@app/analytics/components/DateSelector/utils";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import DropdownMenuItem from "@rui/Dropdown/DropdownMenuItem";
import { Column } from "@rui/Stack";

import {
  GROUPED_PRESETS,
  GroupedRangePreset,
  getStartAndEndDatesFromRangePreset,
} from "./utils";

export interface DateSelectorChangeEvent {
  rangePreset: GroupedRangePreset;
  startDate: DateTime;
  endDate: DateTime;
  rangePresetLabel: string;
}

interface DateSelectorProps {
  value?: GroupedRangePreset | null;
  onChange: (event: DateSelectorChangeEvent) => void;
}

const FLATTENED_PRESETS = flatten(Object.values(GROUPED_PRESETS));

const getDateSelectorLabel = (value: DateSelectorProps["value"]) => {
  if (!value) return "Select a date range";

  const { startDate, endDate } = getStartAndEndDatesFromRangePreset(value);
  return getDateLabel(startDate, endDate);
};

const getRangeLabel = (value: DateSelectorProps["value"]) => {
  if (!value) return null;
  const matchingPreset = find({ rangePreset: value }, FLATTENED_PRESETS);
  if (!matchingPreset) return null;
  return replace(/\(.+\)/, "", matchingPreset.rangePresetLabel);
};

const DateSelector = ({ value, onChange }: DateSelectorProps) => {
  const {
    anchorElement,
    open: menuOpen,
    onClose: onMenuClose,
    onClick: onMenuClick,
  } = useAnchorElement();

  const handleSelect = (newRange: DateSelectorChangeEvent) => {
    if (!newRange) return;
    onChange(newRange);
  };

  const dateLabel = getDateSelectorLabel(value);
  const rangeLabel = getRangeLabel(value);

  return (
    <>
      <ButtonGroup>
        <MenuButton
          onClick={onMenuClick}
          aria-haspopup
          aria-expanded={menuOpen ? "true" : "false"}
        >
          {rangeLabel}
        </MenuButton>
        <MenuButton onClick={onMenuClick} disabled startIcon={<CalendarIcon />}>
          {dateLabel}
        </MenuButton>
      </ButtonGroup>

      <Menu
        anchorEl={anchorElement}
        open={menuOpen}
        onClick={onMenuClose}
        onClose={onMenuClose}
      >
        <Column paddingLeft={1} paddingTop={1}>
          {Object.entries(GROUPED_PRESETS).map(([group, options], i) => (
            <Column key={group} sx={{ minWidth: "274px" }}>
              <Typography
                variant="bodySecondaryMed"
                sx={{ mt: i > 0 ? 1 : 0, mb: 0.5 }}
              >
                By {capitalize(group)}
              </Typography>
              {options.map((option) => (
                <DropdownMenuItem
                  key={option.rangePreset}
                  selected={option.rangePreset === value}
                  label={option.rangePresetLabel}
                  onClick={() => handleSelect(option)}
                />
              ))}
            </Column>
          ))}
        </Column>
      </Menu>
    </>
  );
};

export default DateSelector;
